import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { fetchExpertReportById } from './api';
import { expertReportKeys } from './hooks';
import type { BlockResponse } from './types';

/** Чем читается результат: справочником своих значений либо баллом с размерностью */
export type ExpertReportResultShape = {
  /** Справочник, которым оценивается результат; `null` — результат считается баллом */
  possibleValues: string[] | null;
  /** Размерность балла; `null` — размерность не определена */
  unit: string | null;
};

/** Вид результатов одного заключения: итога и каждого его блока */
export type ExpertReportResultShapes = {
  total: ExpertReportResultShape;
  blocks: Map<number, ExpertReportResultShape>;
};

/** Соответствие `reportId → вид результатов заключения` */
export type ExpertReportResultShapesMap = Map<number, ExpertReportResultShapes>;

/** Вид результата, когда о заключении ещё ничего не известно */
export const UNKNOWN_RESULT_SHAPE: ExpertReportResultShape = { possibleValues: null, unit: null };

const collectBlockShapes = (blocks: BlockResponse[], acc: Map<number, ExpertReportResultShape>) => {
  for (const block of blocks) {
    acc.set(block.id, { possibleValues: block.possibleValues, unit: block.unit });
    if (block.children?.length) collectBlockShapes(block.children, acc);
  }
  return acc;
};

/**
 * Загружает карточки указанных экспертных заключений и отдаёт вид их результатов: справочник
 * значений и размерность у итога и у каждого блока.
 *
 * Нужен там, где вид результата определяет саму подачу колонки (отбор выбором из справочника
 * против диапазона баллов), — знать его надо до того, как придут сами результаты. Дерево блоков
 * (`useExpertReportsBlocksMap`) этого не несёт, поэтому берётся карточка заключения; запрашиваются
 * только те заключения, чьи колонки выведены, а кэш — общий с карточкой заключения.
 */
export const useExpertReportsResultShapes = (reportIds: number[]): ExpertReportResultShapesMap => {
  const queries = useQueries({
    queries: reportIds.map((id) => ({
      queryKey: expertReportKeys.detail(id),
      queryFn: () => fetchExpertReportById(id),
      staleTime: 60_000,
    })),
  });

  return useMemo(() => {
    const map: ExpertReportResultShapesMap = new Map();
    queries.forEach((query, index) => {
      const report = query.data;
      if (!report) return;
      map.set(reportIds[index], {
        total: { possibleValues: report.possibleValues, unit: report.unit },
        blocks: collectBlockShapes(report.blocks, new Map()),
      });
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queries.map((query) => query.dataUpdatedAt).join('|'), reportIds.join(',')]);
};
